// modules/BattleUI.js（重构版 - 使用子模块）
import BattleUILayout from './Battle/BattleUILayout.js';
import BattleUIMessage from './Battle/BattleUIMessage.js';
import BattleUIMenu from './Battle/BattleUIMenu.js';
import BattleUITargetSelection from './Battle/BattleUITargetSelection.js';
import BattleUIInput from './Battle/BattleUIInput.js';
import BattleRewardWindow from './Battle/BattleRewardWindow.js';

export default class BattleUI {
    constructor(scene) {
        this.scene = scene;
        this.events = new Phaser.Events.EventEmitter();
        
        // 初始化子模块
        this.layout = new BattleUILayout(scene);
        this.message = new BattleUIMessage(scene, this.layout);
        this.menu = new BattleUIMenu(scene);
        this.targetSelection = new BattleUITargetSelection(scene);
        this.input = new BattleUIInput(scene, this.menu, this.targetSelection, this.events);
        this.rewardWindow = new BattleRewardWindow(scene);
        
        // 为了向后兼容，保留 elements 引用
        this.elements = {
            menu: this.layout.getMenuElements(),
            target: this.targetSelection.elements.target,
            messages: this.layout.getMessageElements(),
            skillMenu: this.menu.elements.skillMenu,
            itemMenu: this.menu.elements.itemMenu
        };
        
        // 交互状态
        this.interactiveEnabled = true;
    }

    create() {
        this.layout.createBattleBackground();
        this.layout.createMessageArea();
        this.menu.createMenu();
        this.menu.createSkillMenu();
        this.menu.createItemMenu();
        this.menu.createWeaponMenu();
        this.targetSelection.createTargetIndicator();
        this.input.setupInput();
        this.rewardWindow.create();
    }

    // ========== 消息显示方法 ==========
    showMessage(message) {
        this.message.showMessage(message);
    }
    
    /**
     * 逐行滚动显示消息（带动画效果）
     * @param {string} message - 消息文本（多行，用\n分隔）
     * @param {number} delayPerLine - 每行显示的延迟（毫秒），默认300
     * @param {Function} onComplete - 完成回调
     */
    showMessageScrolling(message, delayPerLine = 300, onComplete = null) {
        this.message.showMessageScrolling(message, delayPerLine, onComplete);
    }
    
    /**
     * 显示奖励窗口（美观的弹窗）
     * @param {string} rewardMessage - 奖励消息文本（多行，用\n分隔）
     * @param {number} delayPerLine - 每行显示的延迟（毫秒），默认400
     * @param {Function} onComplete - 完成回调
     */
    showRewardWindow(rewardMessage, delayPerLine = 400, onComplete = null) {
        // 奖励窗口内部会处理键盘输入和关闭逻辑
        this.rewardWindow.showRewards(rewardMessage, delayPerLine, onComplete);
    }

    showPlayerTurn() {
        this.menu.resetSelection();
        this.targetSelection.clearTargetSelection();
        this.menu.hideSkillMenu();
        this.menu.hideItemMenu();
        this.menu.hideWeaponMenu();
        // 更新菜单选项（检查玩家是否在战车中）
        this.menu.updateMenuOptions();
        this.message.showPlayerTurn();
    }

    showEnemyTurn() {
        this.targetSelection.clearTargetSelection();
        this.message.showEnemyTurn();
    }

    showVictory() {
        this.targetSelection.clearTargetSelection();
        this.message.showVictory();
    }

    showDefeat() {
        this.targetSelection.clearTargetSelection();
        this.menu.hideSkillMenu();
        this.menu.hideItemMenu();
        this.message.showDefeat();
    }

    // ========== 目标选择方法 ==========
    showTargetSelection() {
        this.menu.selectingSkill = false;
        this.menu.selectingItem = false;
        this.targetSelection.showTargetSelection();
        this.showMessage('选择目标 (方向键选择, ENTER确认, ESC取消)');
    }

    showRangeTargetSelection(range = 2) {
        this.menu.selectingSkill = false;
        this.menu.selectingItem = false;
        this.targetSelection.showRangeTargetSelection(range);
        this.showMessage('范围攻击 - 选择中心目标');
    }

    clearTargetSelection() {
        this.targetSelection.clearTargetSelection();
    }

    // ========== 技能菜单方法 ==========
    showSkillSelection(skills, currentCost, maxCost) {
        this.menu.showSkillSelection(skills, currentCost, maxCost);
        this.showMessage('选择技能 (W/S:移动, 空格:确认, ESC:取消)');
    }

    hideSkillMenu() {
        this.menu.hideSkillMenu();
    }

    // ========== 道具菜单方法 ==========
    showItemSelection(items) {
        this.menu.showItemSelection(items);
        this.showMessage('选择道具 (W/S:移动, 空格:使用, ESC:取消)');
    }

    hideItemMenu() {
        this.menu.hideItemMenu();
    }
    
    // ========== 武器菜单方法 ==========
    showWeaponSelection(weapons, weaponType) {
        this.menu.showWeaponSelection(weapons, weaponType);
        this.showMessage('选择武器 (W/S:移动, 空格:确认, ESC:取消)');
    }
    
    hideWeaponMenu() {
        this.menu.hideWeaponMenu();
    }

    // ========== 交互状态方法 ==========
    setInteractive(enabled) {
        // 禁用/启用所有菜单选项
        if (this.elements.menu.optionTexts) {
            this.elements.menu.optionTexts.forEach((text, index) => {
                text.setAlpha(enabled ? 1 : 0.5);
            });
        }
        
        // 存储交互状态
        this.interactiveEnabled = enabled;
        
        // 更新菜单选中状态显示（等待时不显示绿色，轮到操作时才显示）
        if (this.menu) {
            this.menu.updateOptionSelection();
        }
    }
    
    isInteractive() {
        return this.interactiveEnabled !== false;
    }

    // ========== 更新方法 ==========
    update(time, delta) {
        // 更新UI动画效果（箭头闪烁）
        this.targetSelection.updateArrowAnimation(time);
    }

    // ========== 清理方法 ==========
    destroy() {
        if (this.input) {
            this.input.destroyInput();
        }
        // 清理所有UI元素
        if (this.layout) {
            const menuElements = this.layout.getMenuElements();
            if (menuElements.background && menuElements.background.destroy) {
                menuElements.background.destroy();
            }
            if (menuElements.border && menuElements.border.destroy) {
                menuElements.border.destroy();
            }
        }
        
        if (this.menu) {
            // 清理菜单选项
            if (this.elements.menu.optionTexts) {
                this.elements.menu.optionTexts.forEach(text => {
                    if (text && text.destroy) text.destroy();
                });
            }
            
            // 清理技能菜单
            if (this.menu.elements.skillMenu) {
                if (this.menu.elements.skillMenu.background && this.menu.elements.skillMenu.background.destroy) {
                    this.menu.elements.skillMenu.background.destroy();
                }
                if (this.menu.elements.skillMenu.border && this.menu.elements.skillMenu.border.destroy) {
                    this.menu.elements.skillMenu.border.destroy();
                }
                if (this.menu.elements.skillMenu.title && this.menu.elements.skillMenu.title.destroy) {
                    this.menu.elements.skillMenu.title.destroy();
                }
                if (this.menu.elements.skillMenu.costText && this.menu.elements.skillMenu.costText.destroy) {
                    this.menu.elements.skillMenu.costText.destroy();
                }
                this.menu.elements.skillMenu.skillTexts.forEach(text => {
                    if (text && text.destroy) text.destroy();
                });
            }
            
            // 清理道具菜单
            if (this.menu.elements.itemMenu) {
                if (this.menu.elements.itemMenu.background && this.menu.elements.itemMenu.background.destroy) {
                    this.menu.elements.itemMenu.background.destroy();
                }
                if (this.menu.elements.itemMenu.border && this.menu.elements.itemMenu.border.destroy) {
                    this.menu.elements.itemMenu.border.destroy();
                }
                if (this.menu.elements.itemMenu.title && this.menu.elements.itemMenu.title.destroy) {
                    this.menu.elements.itemMenu.title.destroy();
                }
                if (this.menu.elements.itemMenu.descriptionText && this.menu.elements.itemMenu.descriptionText.destroy) {
                    this.menu.elements.itemMenu.descriptionText.destroy();
                }
                this.menu.elements.itemMenu.itemTexts.forEach(text => {
                    if (text && text.destroy) text.destroy();
                });
            }
        }
        
        if (this.targetSelection) {
            // 清理目标选择
            if (this.targetSelection.elements.target.arrow && this.targetSelection.elements.target.arrow.destroy) {
                this.targetSelection.elements.target.arrow.destroy();
            }
            if (this.targetSelection.elements.target.info && this.targetSelection.elements.target.info.destroy) {
                this.targetSelection.elements.target.info.destroy();
            }
            this.targetSelection.clearRangeIndicators();
        }
        
        if (this.layout) {
            const messageElements = this.layout.getMessageElements();
            if (messageElements.text && messageElements.text.destroy) {
                messageElements.text.destroy();
            }
        }
        
        // 清理奖励窗口
        if (this.rewardWindow) {
            this.rewardWindow.destroy();
        }
        
        // 重置 elements
        this.elements = {
            menu: {},
            target: {},
            messages: {},
            skillMenu: {},
            itemMenu: {}
        };
        
        // 移除事件监听器
        this.events.removeAllListeners();
    }

}
